/**
 * Quando renovar a sessão e quando encerrá-la.
 *
 * Ficam fora do hook porque são a regra que decide se o usuário continua
 * logado — e dentro do `useAuth` só dava para exercitá-la montando o app
 * inteiro, com router, socket e toast. Aqui são duas funções puras.
 *
 * ## O que elas consertam
 *
 * Antes: qualquer resposta 401, de qualquer rota, apagava o token e deslogava.
 * O app monta `WhatsAppsProvider` e `LoggedInLayout` fora do `isPrivate` (ver
 * routes/index.js), então esses componentes disparam `/settings`, `/whatsapp` e
 * afins ANTES de o auth terminar de resolver. Um 401 desses derrubava a sessão
 * que o login acabara de criar — o "acabei de logar e deu erro", intermitente,
 * decidido por quem respondesse primeiro.
 *
 * E a renovação automática só tratava 403, enquanto o backend responde 401
 * (`ERR_SESSION_EXPIRED`) quando o access token vence: o caminho que salva a
 * sessão nunca rodava, só o que a destruía.
 */

/** O erro veio do próprio pedido de renovação? */
export function ehPedidoDeRenovacao(config) {
  return typeof config?.url === "string" && config.url.includes("/auth/refresh_token");
}

/**
 * Deve tentar renovar o token e repetir o request?
 *
 * Só uma vez por request (`_retry`), e nunca para o próprio refresh — senão
 * um refresh recusado chamaria refresh de novo, em laço.
 */
export function devePedirRenovacao(status, config) {
  if (status !== 401 && status !== 403) return false;
  if (!config || config._retry) return false;
  return !ehPedidoDeRenovacao(config);
}

/**
 * Deve encerrar a sessão (apagar token, marcar como deslogado)?
 *
 * Só quando quem recusou foi a RENOVAÇÃO. Aí não há mais o que tentar: o
 * refresh token venceu ou foi invalidado, e a sessão acabou de verdade.
 */
export function deveEncerrarSessao(status, config) {
  return status === 401 && ehPedidoDeRenovacao(config);
}

/**
 * Sai para o login recarregando a página.
 *
 * `history.push` mantinha o JavaScript da sessão anterior vivo: scripts,
 * hooks e cache de plugins (`frontendScript`, `pluginsCache`) da empresa de
 * quem saiu continuavam rodando para quem entrasse depois na mesma aba — com
 * chamadas indo ao container do plugin da empresa anterior (auditoria
 * 14-09-2026). Recarregar zera tudo isso de uma vez.
 */
export function sairParaLogin(location = window.location) {
  location.assign("/login");
}
